import { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Skeleton,
  TextField,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import LoadingButton from "@mui/lab/LoadingButton";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import AddIcon from "@mui/icons-material/Add";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import EditNoteIcon from "@mui/icons-material/EditNote";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { useScriptMode } from "../../../contexts/ScriptModeContext";
import { useToast } from "../../../components/ui/Toast";
import {
  useBulkCreateMuzakeresMutation,
  useDeleteMuzakereMutation,
  useListGroupMuzakeresQuery,
  useSetMemberMuzakereDoneMutation,
} from "../../../api/muzakereApi";
import { canMarkAttendance, toggleDoneIds } from "./memberEntry/muzakereAttendance";
import { todayString } from "./memberEntry/memberDayHelpers";
import {
  downloadMuzakereTemplate,
  MUZAKERE_HEADERS,
  parseMuzakereWorkbook,
} from "../../../lib/muzakereExcel";
import {
  canEditQuestions,
  canStartAssessment,
  effectiveState,
  hasAssessment,
  startBlockReason,
} from "../../../lib/assessment";
import MuzakereWizardDrawer from "../../muzakere/MuzakereWizardDrawer";
import StartAssessmentDialog from "../../muzakere/StartAssessmentDialog";
import AssessmentResultsDrawer from "../../muzakere/AssessmentResultsDrawer";

const formatDay = (ymd) => {
  const [y, m, d] = String(ymd || "").split("-");
  return y && m && d ? `${d}.${m}.${y}` : "";
};

/**
 * Grup detayındaki Müzakere sekmesi: müzakere listesi (katılım sayısıyla),
 * silme, "Müzakere Ekle" sihirbazı ve Excel'den toplu yükleme. Excel'de
 * yalnızca örnek dosyadaki format kabul edilir; hatalı dosya satır numaralı
 * hatalarla reddedilir, geçerli dosya önizlenip onayla eklenir. Müzakere
 * düzenlenmez. Satıra basınca katılım listesi açılır: günü gelmiş ya da
 * geçmiş müzakerede isme basarak kardeş katıldı/katılmadı işaretlenir.
 * Değerlendirmeli müzakerede üyeler soruları kendileri cevaplar (işaret yok);
 * satırdan sorular düzenlenir, değerlendirme başlatılır, sonuçlar açılır.
 */

/** Değerlendirme durumunun kısa adı (satırın alt bilgisi). */
const ASSESSMENT_STATE_KEY = {
  ready: "mza_state_ready",
  open: "mza_state_open",
  closed: "mza_state_closed",
};

const fullName = (m) => `${m.name || ""} ${m.surname || ""}`.trim() || m.email || "";

/**
 * 2. ve sonraki sayfalar: her sayfa kendi sorgusuyla (silme ve işaretlemede
 * hepsi birlikte tazelenir). Son sayfa "daha fazla" bilgisini üst bileşene verir.
 */
function MorePage({ groupId, q, page, renderRow, onHasMore }) {
  const { data, isFetching } = useListGroupMuzakeresQuery({ groupId, q, page });
  const rows = data?.data?.muzakeres || [];
  const hasMore = Boolean(data?.data?.hasMore);
  useEffect(() => {
    if (data) onHasMore(page, hasMore);
  }, [data, hasMore, page, onHasMore]);
  if (!data && isFetching) {
    return (
      <li className="py-3">
        <Skeleton variant="rounded" height={48} />
      </li>
    );
  }
  return rows.map((m) => renderRow(m));
}

export default function GroupMuzakerePanel({ groupId }) {
  const { t } = useScriptMode();
  const toast = useToast();
  const fileRef = useRef(null);
  // Başlıkta arama (yazarken kısa gecikmeyle) ve 20'şerlik sayfalar.
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [pageCount, setPageCount] = useState(1);
  const [hasMoreByPage, setHasMoreByPage] = useState({});
  useEffect(() => {
    const handle = setTimeout(() => setQ(search.trim()), 300);
    return () => clearTimeout(handle);
  }, [search]);
  useEffect(() => {
    setPageCount(1);
    setHasMoreByPage({});
  }, [q, groupId]);
  const reportHasMore = useCallback(
    (page, more) => setHasMoreByPage((prev) => (prev[page] === more ? prev : { ...prev, [page]: more })),
    []
  );
  const { data, isLoading, isFetching } = useListGroupMuzakeresQuery(
    { groupId, q, page: 1 },
    { skip: !groupId }
  );
  const [bulkCreate, { isLoading: uploading }] = useBulkCreateMuzakeresMutation();
  const [deleteMuzakere, { isLoading: deleting }] = useDeleteMuzakereMutation();
  const [setMemberDone] = useSetMemberMuzakereDoneMutation();
  const today = todayString();
  const [expandedId, setExpandedId] = useState(null);
  // İşaret hemen görünsün; sunucu reddederse geri alınır.
  const [overrides, setOverrides] = useState({});

  const [fileErrors, setFileErrors] = useState([]);
  const [preview, setPreview] = useState(null); // { fileName, rows }
  const [pendingDelete, setPendingDelete] = useState(null);
  // Müzakere Ekle sihirbazı; questionsFor: soruları düzenlenen müzakere.
  const [wizardOpen, setWizardOpen] = useState(false);
  const [questionsFor, setQuestionsFor] = useState(null);
  const [startFor, setStartFor] = useState(null);
  const [resultsFor, setResultsFor] = useState(null);

  const muzakeres = data?.data?.muzakeres || [];
  const lastHasMore =
    pageCount === 1 ? Boolean(data?.data?.hasMore) : Boolean(hasMoreByPage[pageCount]);
  const members = data?.data?.members || [];
  const memberCount = members.length;
  const doneIdsOf = (m) => overrides[m.id] ?? m.doneUserIds ?? [];

  const toggleMember = async (m, memberId) => {
    const before = doneIdsOf(m);
    const done = !before.map(String).includes(String(memberId));
    setOverrides((prev) => ({ ...prev, [m.id]: toggleDoneIds(before, memberId, done) }));
    const result = await setMemberDone({ muzakereId: m.id, memberId, done, today, groupId });
    if (result.error) {
      setOverrides((prev) => ({ ...prev, [m.id]: before }));
      toast.error(result.error.message || t("Hata"));
    }
  };

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = ""; // aynı dosya tekrar seçilebilsin
    if (!file) return;
    setFileErrors([]);
    if (!/\.xlsx$/i.test(file.name)) {
      setFileErrors([t("muzakere_upload_only_xlsx")]);
      return;
    }
    const buffer = await file.arrayBuffer();
    const { rows, errors } = parseMuzakereWorkbook(new Uint8Array(buffer));
    if (errors.length) {
      setFileErrors(errors);
      return;
    }
    setPreview({ fileName: file.name, rows });
  };

  const confirmUpload = async () => {
    if (!preview) return;
    const result = await bulkCreate({ groupId, rows: preview.rows });
    if (result.error) {
      // Sunucu da satır bazlı doğruluyor; mesajı olduğu gibi göster.
      setPreview(null);
      setFileErrors([result.error.message || t("muzakere_upload_error")]);
      return;
    }
    toast.success(
      t("muzakere_upload_success").replace("{n}", result.data?.data?.count ?? preview.rows.length)
    );
    setPreview(null);
  };

  // Tekrarlanan seride "following": bu ve sonraki tekrarlar da silinir (geçmişler kalır).
  const confirmDelete = async (scope = "one") => {
    if (!pendingDelete) return;
    const result = await deleteMuzakere({
      muzakereId: pendingDelete.id,
      groupId,
      scope,
      today: todayString(),
    });
    if (result.error) {
      toast.error(result.error.message || t("muzakere_delete_error"));
    } else {
      const count = result.data?.data?.deletedCount || 1;
      toast.success(
        count > 1 ? t("{n} müzakere silindi.", { n: count }) : t("muzakere_deleted")
      );
    }
    setPendingDelete(null);
  };
  const pendingIsSeries = Boolean(pendingDelete?.seriesId);

  const renderRow = (m) => {
    const doneIds = doneIdsOf(m).map(String);
    const done = doneIds.length;
    const open = expandedId === m.id;
    // Değerlendirmeli müzakerede üyeler soruları kendileri cevaplar: yönetici işaretlemez.
    const assessed = hasAssessment(m);
    const markable = !assessed && canMarkAttendance(m.date, today);
    const aState = assessed ? effectiveState(m.assessment.state, m.assessment.closesAt) : "none";
    const showStart = aState === "ready" && canStartAssessment(m.date, today);
    const showResults = aState === "open" || aState === "closed";
    const showEditQuestions = canEditQuestions({ ...m, doneUserIds: doneIds }, today);
    const blocked = startBlockReason(m, today);
    const attended = members.filter((p) => doneIds.includes(String(p.id)));
    const absent = members.filter((p) => !doneIds.includes(String(p.id)));
    const personRow = (p, isDone) =>
      markable ? (
        <li key={p.id}>
          <button
            type="button"
            onClick={() => toggleMember(m, p.id)}
            className="w-full flex items-center gap-3 py-1.5 text-left text-sm text-terakkiInk dark:text-white hover:opacity-80"
            aria-pressed={isDone}
          >
            <span className="flex-1 truncate">{fullName(p)}</span>
            {isDone ? (
              <CheckCircleIcon fontSize="small" className="text-[#2E9E6A] dark:text-[#34B37A]" />
            ) : (
              <RadioButtonUncheckedIcon
                fontSize="small"
                className="text-terakkiBorder dark:text-white/30"
              />
            )}
          </button>
        </li>
      ) : (
        <li key={p.id} className="py-1 text-sm text-terakkiInk dark:text-white/80 truncate">
          {fullName(p)}
        </li>
      );
    return (
      <li key={m.id} className="border-b border-terakkiBorder dark:border-white/10">
        <div className="flex items-center gap-3 py-3">
          <button
            type="button"
            onClick={() => setExpandedId(open ? null : m.id)}
            className="flex min-w-0 flex-1 items-center gap-3 text-left"
            aria-expanded={open}
          >
            <div className="w-20 shrink-0 text-center rounded-lg bg-terakkiBrandCloud dark:bg-white/10 py-2 text-sm font-SemiBold text-terakkiPrimaryDark dark:text-terakkiPrimaryLight">
              {formatDay(m.date)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate font-SemiBold text-terakkiInk dark:text-white">{m.title}</div>
              {m.description ? (
                <div className="truncate text-sm text-terakkiNeutral dark:text-white/60">
                  {m.description}
                </div>
              ) : null}
              {assessed ? (
                <div className="flex items-center gap-1 text-xs text-terakkiNeutral dark:text-white/60">
                  <AssessmentOutlinedIcon sx={{ fontSize: 14 }} />
                  {`${m.assessment.questionCount || 0} ${t("assess_question_word")} · ${t(ASSESSMENT_STATE_KEY[aState] || "mza_state_ready")}`}
                </div>
              ) : null}
            </div>
            <div className="shrink-0 text-sm text-terakkiPrimaryDark dark:text-terakkiPrimaryLight">
              {t("muzakere_attendance").replace("{done}", done).replace("{total}", memberCount)}
            </div>
            <ExpandMoreIcon
              className={`shrink-0 text-terakkiNeutral dark:text-white/60 transition-transform ${
                open ? "rotate-180" : ""
              }`}
            />
          </button>
          {/* Günü geçmiş müzakere silinemez (sunucu da reddeder) */}
          <Tooltip title={m.date < today ? t("muzakere_delete_past") : t("muzakere_delete")}>
            <span>
              <IconButton
                onClick={() => setPendingDelete(m)}
                disabled={m.date < today}
                aria-label={t("muzakere_delete")}
              >
                <DeleteOutlineIcon className="dark:text-white/70" />
              </IconButton>
            </span>
          </Tooltip>
        </div>
        {open ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pb-4 pl-2">
            {showEditQuestions || showStart || showResults || blocked || assessed ? (
              <div className="sm:col-span-2 flex flex-col gap-2 pb-1">
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                  {showEditQuestions ? (
                    <button
                      type="button"
                      onClick={() => setQuestionsFor({ id: m.id, title: m.title })}
                      className="flex items-center gap-1 text-sm text-terakkiPrimaryDark dark:text-[#8FC9CA]"
                    >
                      <EditNoteIcon fontSize="small" />
                      {t(assessed ? "mza_edit_questions" : "mza_add_questions")}
                    </button>
                  ) : null}
                  {showStart ? (
                    <button
                      type="button"
                      onClick={() => setStartFor(m)}
                      className="flex items-center gap-1 text-sm text-[#2E9E6A] dark:text-[#7BE3A8]"
                    >
                      <PlayCircleOutlineIcon fontSize="small" />
                      {t("mza_start_title")}
                    </button>
                  ) : null}
                  {showResults ? (
                    <button
                      type="button"
                      onClick={() => setResultsFor({ id: m.id, title: m.title })}
                      className="flex items-center gap-1 text-sm text-terakkiPrimaryDark dark:text-[#8FC9CA]"
                    >
                      <AssessmentOutlinedIcon fontSize="small" />
                      {t("mza_results")}
                    </button>
                  ) : null}
                </div>
                {blocked ? (
                  <p className="flex items-center gap-1.5 text-xs text-terakkiNeutral dark:text-white/60">
                    <ScheduleIcon sx={{ fontSize: 14 }} />
                    {t(blocked === "notYet" ? "mza_start_not_yet" : "mza_start_expired")}
                  </p>
                ) : null}
                {assessed ? (
                  <p className="flex items-center gap-1.5 text-xs text-terakkiNeutral dark:text-white/60">
                    <LockOutlinedIcon sx={{ fontSize: 14 }} />
                    {t("mza_members_answer")}
                  </p>
                ) : null}
              </div>
            ) : null}
            <div>
              <div className="text-xs font-SemiBold text-terakkiNeutral dark:text-white/60 mb-1">
                {`${t("Katılanlar")} · ${attended.length}`}
              </div>
              {attended.length === 0 ? (
                <p className="text-sm text-terakkiNeutral dark:text-white/60">{t("Henüz katılan yok.")}</p>
              ) : (
                <ul>{attended.map((p) => personRow(p, true))}</ul>
              )}
            </div>
            <div>
              <div className="text-xs font-SemiBold text-terakkiNeutral dark:text-white/60 mb-1">
                {`${t("Katılmayanlar")} · ${absent.length}`}
              </div>
              {absent.length === 0 ? (
                <p className="text-sm text-terakkiNeutral dark:text-white/60">{t("Herkes katıldı.")}</p>
              ) : (
                <ul>{absent.map((p) => personRow(p, false))}</ul>
              )}
            </div>
            <p className="sm:col-span-2 text-xs text-terakkiNeutral dark:text-white/50">
              {assessed
                ? t("mza_attendance_by_answers")
                : markable
                  ? t("İsme basarak katıldı ya da katılmadı olarak işaretleyebilirsiniz.")
                  : t("Bu müzakerenin günü henüz gelmedi.")}
            </p>
          </div>
        ) : null}
      </li>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-lg font-SemiBold text-terakkiInk dark:text-white">{t("muzakere_title")}</h3>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setWizardOpen(true)}>
            {t("mzw_add_title")}
          </Button>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={downloadMuzakereTemplate}>
            {t("muzakere_template_download")}
          </Button>
          <Button
            variant="outlined"
            startIcon={<UploadFileIcon />}
            onClick={() => fileRef.current?.click()}
          >
            {t("muzakere_upload")}
          </Button>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="hidden"
            onChange={handleFile}
          />
        </div>
      </div>

      <p className="text-sm text-terakkiNeutral dark:text-white/60">
        {t("muzakere_upload_hint").replace("{headers}", MUZAKERE_HEADERS.join(" | "))}
      </p>

      {fileErrors.length > 0 && (
        <div className="rounded-xl border border-terakkiError/30 bg-terakkiError/5 p-3">
          <div className="flex items-center gap-2 text-terakkiError font-SemiBold text-sm">
            <ErrorOutlineIcon fontSize="small" />
            {t("muzakere_upload_rejected")}
          </div>
          <ul className="mt-2 list-disc pl-6 text-sm text-terakkiInk dark:text-white">
            {fileErrors.slice(0, 10).map((e) => (
              <li key={e}>{e}</li>
            ))}
            {fileErrors.length > 10 && (
              <li>{t("muzakere_more_errors").replace("{n}", fileErrors.length - 10)}</li>
            )}
          </ul>
        </div>
      )}

      <TextField
        size="small"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={t("Başlıkta ara")}
        inputProps={{ "aria-label": t("Başlıkta ara"), maxLength: 100 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" className="text-terakkiNeutral dark:text-white/60" />
            </InputAdornment>
          ),
        }}
        sx={{ maxWidth: 360 }}
      />

      {isLoading || (isFetching && !data) ? (
        <div className="flex flex-col gap-2">
          <Skeleton variant="rounded" height={56} />
          <Skeleton variant="rounded" height={56} />
        </div>
      ) : muzakeres.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-8 text-terakkiNeutral dark:text-white/60">
          <ForumOutlinedIcon />
          <p className="text-sm">{q ? t("Aramaya uyan müzakere yok.") : t("muzakere_empty")}</p>
        </div>
      ) : (
        <ul className="tk-stagger flex flex-col border-t border-terakkiBorder dark:border-white/10">
          {muzakeres.map((m) => renderRow(m))}
          {Array.from({ length: pageCount - 1 }, (_, i) => (
            <MorePage
              key={`${q}-${i + 2}`}
              groupId={groupId}
              q={q}
              page={i + 2}
              renderRow={renderRow}
              onHasMore={reportHasMore}
            />
          ))}
        </ul>
      )}
      {!isLoading && muzakeres.length > 0 && lastHasMore ? (
        <div className="flex justify-center">
          <Button
            variant="text"
            onClick={() => setPageCount((n) => n + 1)}
            sx={{ textTransform: "none", borderRadius: 999 }}
          >
            {t("Daha fazla")}
          </Button>
        </div>
      ) : null}

      {/* Müzakere Ekle ve soru düzenleme */}
      <MuzakereWizardDrawer
        open={wizardOpen || !!questionsFor}
        groupId={groupId}
        questionsFor={questionsFor}
        onClose={() => {
          setWizardOpen(false);
          setQuestionsFor(null);
        }}
      />
      {startFor ? (
        <StartAssessmentDialog muzakere={startFor} groupId={groupId} onClose={() => setStartFor(null)} />
      ) : null}
      <AssessmentResultsDrawer open={!!resultsFor} muzakere={resultsFor} onClose={() => setResultsFor(null)} />

      {/* Yükleme önizlemesi */}
      <Dialog open={!!preview} onClose={() => !uploading && setPreview(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("muzakere_preview_title")}</DialogTitle>
        <DialogContent>
          <p className="text-sm text-terakkiNeutral mb-3 dark:text-white/70">
            {t("muzakere_preview_body")
              .replace("{file}", preview?.fileName || "")
              .replace("{n}", preview?.rows.length || 0)}
          </p>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-terakkiNeutral dark:text-white/70">
                {MUZAKERE_HEADERS.map((h) => (
                  <th key={h} className="py-1 pr-3 font-SemiBold">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(preview?.rows || []).slice(0, 10).map((r, i) => (
                <tr key={i} className="border-t border-terakkiBorder">
                  <td className="py-1 pr-3">{r.title}</td>
                  <td className="py-1 pr-3 whitespace-nowrap">{formatDay(r.date)}</td>
                  <td className="py-1 pr-3 text-terakkiNeutral dark:text-white/70">{r.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {(preview?.rows.length || 0) > 10 && (
            <p className="text-xs text-terakkiNeutral mt-2 dark:text-white/70">
              {t("muzakere_preview_more").replace("{n}", preview.rows.length - 10)}
            </p>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreview(null)} disabled={uploading} sx={{ color: "#C0392B", bgcolor: "rgba(192,57,43,0.08)", "&:hover": { bgcolor: "rgba(192,57,43,0.14)" }, ".dark &": { color: "#F2A59C", bgcolor: "rgba(242,165,156,0.14)" } }}>
            {t("muzakere_cancel")}
          </Button>
          <LoadingButton variant="contained" loading={uploading} onClick={confirmUpload}>
            {t("muzakere_preview_confirm")}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Silme onayı */}
      <Dialog open={!!pendingDelete} onClose={() => setPendingDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t("muzakere_delete_title")}</DialogTitle>
        <DialogContent>
          <p className="text-sm text-terakkiInk dark:text-white">
            {t("muzakere_delete_body").replace("{title}", pendingDelete?.title || "")}
          </p>
          {pendingIsSeries ? (
            <p className="mt-2 text-sm text-terakkiNeutral dark:text-white/60">
              {t("Bu müzakere tekrarlanan bir serinin parçası. Yalnız bunu mu, yoksa bunu ve sonraki tekrarları mı silmek istersiniz? Geçmiş günler korunur.")}
            </p>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ flexWrap: "wrap", gap: 1 }}>
          <Button onClick={() => setPendingDelete(null)} disabled={deleting} sx={{ color: "#C0392B", bgcolor: "rgba(192,57,43,0.08)", "&:hover": { bgcolor: "rgba(192,57,43,0.14)" }, ".dark &": { color: "#F2A59C", bgcolor: "rgba(242,165,156,0.14)" } }}>
            {t("muzakere_cancel")}
          </Button>
          {pendingIsSeries ? (
            <>
              <Button color="error" variant="outlined" onClick={() => confirmDelete("one")} disabled={deleting}>
                {t("Yalnız bunu")}
              </Button>
              <Button color="error" variant="contained" onClick={() => confirmDelete("following")} disabled={deleting}>
                {t("Bu ve sonrakileri")}
              </Button>
            </>
          ) : (
            <Button color="error" variant="contained" onClick={() => confirmDelete("one")} disabled={deleting}>
              {t("muzakere_delete")}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </div>
  );
}
